import styles from "./low-rate-cash-back.module.css"

import React from "react"
import assign from "object-assign"
import * as util from "activities/util"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import * as callouts from "activities/components/callouts"
import {DoubleBar} from "story/components/graphs/bar"
import * as graphUtil from "shared/graphs/util"
import cx from "classnames"
import AccessibilityTable from "activities/components/shared/accessibility-table"

const getResults = (carPrice, downPayment, term, rate, cashBackOffer, lowRateOffer, tradeIn, amountOwed) => {
  const lowRatePrincipal = carPrice - downPayment - tradeIn + amountOwed
  const cashBackPrincipal = carPrice - downPayment - cashBackOffer - tradeIn + amountOwed

  const lowRateInterest = lowRatePrincipal * (lowRateOffer / 12) * term
  const cashBackInterest = cashBackPrincipal * (rate / 12) * term

  const lowRateMonthlyPayment = (lowRatePrincipal + lowRateInterest) / term
  const cashBackMonthlyPayment = (cashBackPrincipal + cashBackInterest) / term

  const lowRateLoanAmount = lowRatePrincipal + lowRateInterest
  const cashBackLoanAmount = cashBackPrincipal + cashBackInterest

  const result = lowRateLoanAmount > cashBackLoanAmount ? "cash back" : "low rate"
  const difference = Math.abs(lowRateLoanAmount - cashBackLoanAmount)
  const resultSentence = `Over the period of the loan, the ${result} option will save you ${util.formatDollarsNoCents(difference)}.`
  return {
    lowRatePrincipal,
    cashBackPrincipal,
    lowRateInterest,
    cashBackInterest,
    lowRateMonthlyPayment,
    cashBackMonthlyPayment,
    lowRateLoanAmount,
    cashBackLoanAmount,
    resultSentence
  }
}

export default shared.ActivityFactory(
  ({getProps, getState, updateState}) => {
    const {carPrice, downPayment, term, rate, cashBackOffer, lowRateOffer, tradeIn, amountOwed, resetKey} = getState()
    const {id, theme} = getProps()
    const isDisabled = carPrice <= downPayment

    const decimalLowRate = lowRateOffer / 100
    const decimalRate = rate / 100

    const {
      lowRatePrincipal,
      cashBackPrincipal,
      lowRateInterest,
      cashBackInterest,
      lowRateMonthlyPayment,
      cashBackMonthlyPayment,
      lowRateLoanAmount,
      cashBackLoanAmount,
      resultSentence
    } = getResults(carPrice, downPayment, term, decimalRate, cashBackOffer, decimalLowRate, tradeIn, amountOwed)

    const getData = () => {
      const results = [
        [lowRatePrincipal, lowRateInterest],
        [cashBackPrincipal, cashBackInterest]
      ]
      const orZero = (num) => Math.max(num, 0)
      return ["Low Rate", "Cash Back"].reduce((memo, label, idx) => {
        memo.push([label, orZero(results[idx][0]), orZero(results[idx][1])])
        return memo
      }, [])
    }

    const accessibilityTableHeadings = [
      {name: "Offer", key: "offer"},
      {name: "Principal", key: "principal"},
      {name: "Interest", key: "interest"},
      {name: "Total Paid", key: "totalPaid"}
    ]

    const accessibilityTableData = getData().map(([offer, principal, interest]) => ({
      offer,
      principal: util.formatDollarsNoCents(principal),
      interest: util.formatDollarsNoCents(interest),
      totalPaid: util.formatDollarsNoCents(principal + interest)
    }))

    const accessibilityTableLabel = "Low Rate vs Cash Back Table"

    return (
      <div className={styles["calc"]}>
        <shared.Content className={styles["content"]}>
          <shared.SummaryCalcContainer>
            <shared.InputsContainer className={styles["scrollable"]}>
              <div className={cx(styles["input-section"], styles["first"])}>
                <inputs.Label className="overline" inputId="new-car-information">
                  New Car Information
                </inputs.Label>
                <div className={styles["inputs-group"]}>
                  <inputs.Input id={id} title="Car Price" onChange={updateState("carPrice", util.fromString)} value={carPrice} resetKey={resetKey} />
                  <inputs.Input id={id} title="Down Payment" onChange={updateState("downPayment", util.fromString)} value={downPayment} resetKey={resetKey} />
                  <inputs.Input id={id} title="Loan Term (months)" onChange={updateState("term", util.fromString)} value={term} valueType="number" resetKey={resetKey} />
                  <inputs.RangeInput
                    id={id}
                    title="Interest Rate"
                    onChange={updateState("rate", parseFloat)}
                    initialValue={rate}
                    value={rate}
                    min={0}
                    max={10.0}
                    step={0.05}
                    units="%"
                    valueType="percent"
                    disabled={isDisabled}
                    resetKey={resetKey}
                    color={theme["color-1"]}
                  />
                </div>
              </div>
              <div className={styles["input-section"]}>
                <inputs.Label className="overline" inputId="offer">
                  Offer
                </inputs.Label>
                <div className={styles["inputs-group"]}>
                  <inputs.Input id={id} title="Cash Back" onChange={updateState("cashBackOffer", util.fromString)} value={cashBackOffer} resetKey={resetKey} />
                  <inputs.RangeInput
                    id={id}
                    title="Low Interest Rate Offer"
                    onChange={updateState("lowRateOffer", parseFloat)}
                    initialValue={lowRateOffer}
                    value={lowRateOffer}
                    min={0}
                    max={10.0}
                    step={0.05}
                    units="%"
                    valueType="percent"
                    disabled={isDisabled}
                    resetKey={resetKey}
                    color={theme["color-1"]}
                  />
                </div>
              </div>
              <div className={styles["input-section"]}>
                <inputs.Label className="overline" inputId="trade-in">
                  Trade-In
                </inputs.Label>
                <div className={styles["inputs-group"]}>
                  <inputs.Input id={id} title="Trade-In Value" onChange={updateState("tradeIn", util.fromString)} value={tradeIn} resetKey={resetKey} />
                  <inputs.Input id={id} title="Amount Owed" onChange={updateState("amountOwed", util.fromString)} value={amountOwed} resetKey={resetKey} />
                </div>
              </div>
            </shared.InputsContainer>
            <shared.ChartContainer>
              <DoubleBar
                isStacked={true}
                yAxis="Amount Paid"
                xAxis={""}
                values={getData()}
                tooltip={[
                  "",
                  {name: "Principal", type: "currency", color: graphUtil.getColor(theme, 1)},
                  {name: "Interest", type: "currency", color: graphUtil.getColor(theme, 4)}
                ]}
                type="calculator"
                ariaLabel="This bar graph visually represents the difference between how much you will pay for your loan with a cash back offer and how much you will pay with a low rate offer."
                colors={[graphUtil.getColor(theme, 1), graphUtil.getColor(theme, 4)]}
                formatter="currency"
                barSize={180}
                customXAxisStyles={{fontSize: "12", fontFamily: "Cardinal Grotesque", fill: "#5c5761", dy: 8}}
              />
            </shared.ChartContainer>
            <shared.SummaryContainer>
              <div className={styles["result"]}>{resultSentence}</div>
              <callouts.Callout className={styles["callout"]}>
                <callouts.DoubleStat label="Low Rate">
                  <callouts.Detail label="Loan Amount" result={util.formatDollarsNoCents(util.ifNegative(lowRateLoanAmount))} dataTestId="low-rate-loan-amount" />
                  <callouts.Detail label="Monthly Payment" result={util.formatDollarsNoCents(util.ifNegative(lowRateMonthlyPayment))} dataTestId="low-rate-monthly-payment" />
                </callouts.DoubleStat>
                <callouts.DoubleStat label="Cash Back">
                  <callouts.Detail label="Loan Amount" result={util.formatDollarsNoCents(util.ifNegative(cashBackLoanAmount))} dataTestId="cash-back-loan-amount" />
                  <callouts.Detail label="Monthly Payment" result={util.formatDollarsNoCents(util.ifNegative(cashBackMonthlyPayment))} dataTestId="cash-back-monthly-payment" />
                </callouts.DoubleStat>
              </callouts.Callout>
            </shared.SummaryContainer>
          </shared.SummaryCalcContainer>
        </shared.Content>

        <AccessibilityTable headings={accessibilityTableHeadings} data={accessibilityTableData} label={accessibilityTableLabel} />
      </div>
    )
  },
  {
    stateFilter: (state) => {
      const {carPrice, downPayment, term, rate, cashBackOffer, lowRateOffer, tradeIn, amountOwed} = state

      const {lowRateMonthlyPayment, cashBackMonthlyPayment, lowRateLoanAmount, cashBackLoanAmount, resultSentence} = getResults(
        carPrice,
        downPayment,
        term,
        rate,
        cashBackOffer,
        lowRateOffer,
        tradeIn,
        amountOwed
      )

      return assign(state, {
        lowRateMonthlyPayment,
        cashBackMonthlyPayment,
        lowRateLoanAmount,
        cashBackLoanAmount,
        resultSentence
      })
    },
    defaultProps: {
      id: "low-rate-cash-back-calculator",
      className: "low-rate-cash-back-calculator",
      title: "Auto Financing: Low Rate vs Cash Back",
      subtitle: "Buying a car? Compare a lower interest rate to a lower balance (via a cash back incentive)."
    },
    defaultState: {
      carPrice: 30000,
      downPayment: 1000,
      term: 60,
      rate: 12,
      cashBackOffer: 500,
      lowRateOffer: 1.9,
      tradeIn: 5000,
      amountOwed: 0
    }
  }
)
